import SEOHead from '../components/SEOHead';
import BreadcrumbSchema from '../components/BreadcrumbSchema';

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24" aria-labelledby={`${id}-h`}>
    <h2 id={`${id}-h`} className="text-xl text-[#D4AF37] mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{title}</h2>
    <div className="text-white/60 text-sm leading-relaxed font-light space-y-3">{children}</div>
  </section>
);

const Bullet = ({ children, kind = 'gold' }) => (
  <li className="flex items-start gap-2">
    <span className={`text-xs mt-1.5 ${kind === 'gold' ? 'text-[#D4AF37]' : kind === 'red' ? 'text-red-400' : 'text-white/30'}`}>
      {kind === 'red' ? '✕' : '◆'}
    </span>
    <span>{children}</span>
  </li>
);

function PrivacyHardcoded() {
  return (
    <div data-testid="privacy-page" className="min-h-screen">
      <SEOHead
        title="Privacy Policy"
        description="JS Fragrance Privacy Policy — what data we collect, how we use it, your rights, cookies, analytics, payment security, and how to delete your account."
        url="https://jsfragrances.com/privacy"
      />
      <BreadcrumbSchema crumbs={[{ name: 'Home', path: '/' }, { name: 'Privacy Policy', path: '/privacy' }]} />

      <div className="bg-[#0A0A0A] py-12 sm:py-16 border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2 font-sans">Legal</p>
          <h1 className="text-4xl sm:text-5xl tracking-tight leading-none font-light text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Privacy Policy
          </h1>
          <p className="text-white/40 text-sm mt-3 font-sans">Last updated: February 1, 2026</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16 space-y-10">
        <Section id="commitment" title="Our Commitment to Your Privacy">
          <p>
            At JS Fragrance, your trust matters more than any sale. We are fully committed to protecting your personal information
            and ensuring it stays safe, secure, and private.
          </p>
          <p className="text-white/80">
            <strong>We will never sell, leak, share, rent, or trade your personal data with any third party.</strong> Your information
            is used solely to fulfill your orders and improve your shopping experience.
          </p>
        </Section>

        <Section id="info-collected" title="Information We Collect">
          <p>When you create an account, place an order, submit feedback, or contact us, we may collect:</p>
          <ul className="space-y-1.5 ml-1">
            <Bullet>Full name and email address</Bullet>
            <Bullet>Phone number (for delivery coordination)</Bullet>
            <Bullet>Delivery address (street, barangay, city, province, ZIP code)</Bullet>
            <Bullet>Payment proof (screenshot of your GCash / bank / e-wallet transaction)</Bullet>
            <Bullet>Optional profile photo (only if you choose to upload one)</Bullet>
            <Bullet>Google profile basic info (only if you sign in with Google)</Bullet>
            <Bullet>Order history, wishlist, and saved addresses</Bullet>
            <Bullet>Reviews, feedback messages, contact-form submissions, and any photos you attach</Bullet>
            <Bullet>Custom Scent Builder responses (your selected notes/preferences)</Bullet>
          </ul>
        </Section>

        <Section id="ambassador-data" title="Ambassador-Specific Data">
          <p>If you join as an ambassador or distributor, we additionally collect and store:</p>
          <ul className="space-y-1.5 ml-1">
            <Bullet>Your unique referral slug (auto-generated from your name when your account is created)</Bullet>
            <Bullet>Your social media handle and the main platform you&apos;ll promote on</Bullet>
            <Bullet>Audience-size and monthly-sales-estimate ranges (only if you provided them)</Bullet>
            <Bullet>Why-us motivation text from your application form</Bullet>
            <Bullet>Your tester-kit payment proof image (e.g., GCash / bank screenshot)</Bullet>
            <Bullet>The ambassador who referred you (if you joined via someone&apos;s ?ref= link)</Bullet>
            <Bullet>Your ambassador wallet balance and transaction history</Bullet>
            <Bullet>Your manual / chat orders (recorded by admin so they count toward your leaderboard)</Bullet>
            <Bullet>Monthly Top-Seller prize claims you submit (year/month, your bottle count + revenue snapshot, your GCash number and scent-choice note, plus admin&apos;s internal fulfilment notes)</Bullet>
          </ul>
          <p>
            <strong className="text-white/80">What is publicly visible to other ambassadors</strong> on the team leaderboard
            inside the dashboard: your display name, tier badge, total bottles sold, and revenue. Your phone, email, address,
            payment proof, audience size, motivation text, and prize-claim notes are <strong className="text-white/80">never displayed</strong> to other users.
          </p>
          <p>
            <strong className="text-white/80">Wholesale pricing</strong> is stored confidentially and gated behind authentication —
            only verified active ambassadors / distributors / admins can see distributor tier pricing.
          </p>
        </Section>

        <Section id="how-we-use" title="How We Use Your Information">
          <ul className="space-y-1.5 ml-1">
            <Bullet>Process and fulfill your orders, verify payments, and coordinate delivery</Bullet>
            <Bullet>Communicate order updates, shipping notifications, and (with your consent) promotional emails</Bullet>
            <Bullet>Provide customer support when you contact us</Bullet>
            <Bullet>Improve our products, product recommendations, and the website experience</Bullet>
            <Bullet>Detect and prevent fraud, abuse, or unauthorized access</Bullet>
            <Bullet>Calculate ambassador referral commissions when applicable</Bullet>
          </ul>
        </Section>

        <Section id="cookies-tracking" title="Cookies, Analytics, & Tracking">
          <p>We use a small number of technologies to keep the site running smoothly:</p>
          <ul className="space-y-1.5 ml-1">
            <Bullet><strong className="text-white/80">Essential cookies:</strong> session, login state, cart contents, and bot-prevention.</Bullet>
            <Bullet><strong className="text-white/80">Ambassador referral cookies:</strong> when you visit via a referral link, we store the ambassador code (typically 30 days) so the correct person gets credited if you purchase.</Bullet>
            <Bullet><strong className="text-white/80">Referral channel analytics:</strong> when you arrive via a referral link with UTM parameters (e.g., <span className="font-mono text-white/50">?utm_source=tiktok</span>), we log the source, medium, campaign, and the visited path so admins and ambassadors can see which channel (TikTok vs Facebook vs IG vs DM) actually drives traffic. We log only the UTM tags plus a coarse referer/user-agent string — no email, name, or browsing history.</Bullet>
            <Bullet><strong className="text-white/80">Anonymous analytics:</strong> aggregated visitor counts, page views, and conversion funnels — used to improve UX. No individually identifying data is sold to analytics partners.</Bullet>
            <Bullet><strong className="text-white/80">Service worker:</strong> a Progressive Web App cache to make the site fast and work offline. No personal data leaves your device.</Bullet>
            <Bullet><strong className="text-white/80">localStorage:</strong> stores your cart, wishlist, recently-viewed products, your Compare Scents selection (max 3 product IDs), and UI preferences — never transmitted to us unless you take an action that requires it.</Bullet>
          </ul>
          <p>You can clear cookies and local storage at any time via your browser settings.</p>
        </Section>

        <Section id="control" title="Your Account & Your Control">
          <p>You have full control over your account information. From your account page you can:</p>
          <ul className="space-y-1.5 ml-1">
            <Bullet>View and edit your name and phone number at any time</Bullet>
            <Bullet>Upload or remove your profile photo</Bullet>
            <Bullet>Manage your saved delivery addresses</Bullet>
            <Bullet>View your order history, track active orders, and one-click <strong className="text-white/80">Reorder</strong> any past order</Bullet>
            <Bullet>Manage your wishlist, recently-viewed list, and Compare Scents selection</Bullet>
            <Bullet>Request password reset via the Forgot Password page</Bullet>
            <Bullet>Request full account deletion by contacting support (order records may be retained for tax compliance)</Bullet>
          </ul>
        </Section>

        <Section id="passwords" title="Password & Account Security">
          <p>
            Passwords are encrypted using <strong className="text-white/80">bcrypt</strong> hashing. We physically cannot see your real password —
            we can only verify whether a password matches. Admin accounts require <strong className="text-white/80">two-factor authentication (2FA)</strong>.
          </p>
          <p>
            We rate-limit login attempts (8 attempts per IP per 5 minutes) to prevent brute-force attacks.
          </p>
        </Section>

        <Section id="data-protection" title="Data Protection & Security">
          <ul className="space-y-1.5 ml-1">
            <Bullet>All data is transmitted over HTTPS (TLS) — we force-redirect HTTP to HTTPS on every route.</Bullet>
            <Bullet>Passwords are hashed and never stored in plain text.</Bullet>
            <Bullet>Payment proofs are stored in encrypted object storage and only accessible to authorized admins.</Bullet>
            <Bullet>Admin access is logged in a tamper-evident audit log for accountability.</Bullet>
            <Bullet>We regularly review security practices and apply patches promptly.</Bullet>
          </ul>
        </Section>

        <Section id="never" title="We Will Never">
          <ul className="space-y-1.5 ml-1">
            <Bullet kind="red">Sell your personal information to third parties</Bullet>
            <Bullet kind="red">Share your data with advertisers or data brokers</Bullet>
            <Bullet kind="red">Use your data for purposes beyond what&apos;s described in this policy</Bullet>
            <Bullet kind="red">Send you spam or unsolicited messages</Bullet>
            <Bullet kind="red">Expose your payment proof, address, or contact details publicly</Bullet>
          </ul>
        </Section>

        <Section id="google-login" title="Google Sign-In">
          <p>
            If you sign in with Google, we receive your name, email address, and profile picture only. We do not access your contacts,
            calendar, Drive, or any other Google service data.
          </p>
        </Section>

        <Section id="reviews-pii" title="Reviews, Feedback, & Contact Form">
          <p>
            When you submit a review or feedback, the name you provide and your comment text may be displayed publicly on our website
            after admin approval. <strong className="text-white/80">Your email address is never displayed publicly.</strong> Photos are stored
            securely and only published after approval.
          </p>
          <p>
            Contact form submissions are visible only to JS Fragrance admins via the internal Inbox. We respond within 1 business day.
          </p>
        </Section>

        <Section id="children" title="Children's Privacy">
          <p>
            JS Fragrance is intended for users aged 18 and over. We do not knowingly collect personal information from minors.
            If you believe a minor has provided us with personal data, please contact us so we can remove it.
          </p>
        </Section>

        <Section id="rights" title="Your Rights (Philippine Data Privacy Act)">
          <p>Under the Philippine Data Privacy Act of 2012 (RA 10173), you have the right to:</p>
          <ul className="space-y-1.5 ml-1">
            <Bullet>Be informed about how your data is used</Bullet>
            <Bullet>Access, correct, or update the personal data we hold about you</Bullet>
            <Bullet>Object to processing or withdraw consent at any time</Bullet>
            <Bullet>Request deletion of your data</Bullet>
            <Bullet>Lodge a complaint with the National Privacy Commission</Bullet>
          </ul>
        </Section>

        <Section id="contact" title="Contact Us">
          <p>For privacy questions or data requests, reach out:</p>
          <ul className="space-y-1.5 ml-1">
            <Bullet>Email: <a href="mailto:support@jsfragrances.com" className="text-[#D4AF37] hover:underline">support@jsfragrances.com</a></Bullet>
            <Bullet>Contact form: <a href="/about#contact" className="text-[#D4AF37] hover:underline">jsfragrances.com/about#contact</a></Bullet>
            <Bullet>Facebook: <a href="https://www.facebook.com/profile.php?id=61588041885547" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline">JS Fragrance</a></Bullet>
          </ul>
        </Section>

        <div className="border-t border-white/10 pt-8 text-center">
          <p className="text-white/30 text-xs font-sans uppercase tracking-wider">Your data is safe with us. Always.</p>
        </div>
      </div>
    </div>
  );
}

export default function PrivacyPage() {
  return <PrivacyHardcoded />;
}
